import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Users, Calendar, Send, Save, Eye, Bold, Italic, Underline, List, Link, AlignLeft, AlignCenter, AlignRight, Palette, Paperclip, Image } from 'lucide-react';
import { getAllIndividualUsers } from '../../../../api/apiFunction/userServices';
import { previewCampaignEmail } from '../../../../api/apiFunction/newsletterServices';

// Default template content based on email type
const DEFAULT_TEMPLATES = {
  newsletter: `<h2>Hello!</h2>
<p>We're excited to share our latest updates with you.</p>
<h3>What's New?</h3>
<ul>
  <li>Feature 1: Brief description</li>
</ul>
<p>We hope you enjoy these new features!</p>
<p>Best regards,<br>The LeebAI Team</p>`,
  
  announcement: `<h2>Important Announcement</h2>
<p>We have an important update to share with you.</p>
<h3>Key Points:</h3>
<ul>
  <li>Point 1: Details here</li>
</ul>
<p>If you have any questions, please don't hesitate to contact us.</p>
<p>Best regards,<br>The LeebAI Team</p>`,

  customer_notification: `<h2>Hello!</h2>
<p>We wanted to notify you about an important update regarding your account.</p>
<h3>Notification Details:</h3>
<p>Your notification message goes here...</p>
<p>If you need any assistance, our support team is here to help.</p>
<p>Best regards,<br>The LeebAI Team</p>`
};

const AddNewsletterModal = ({ isOpen, onClose, onSave, campaign, isEditing }) => {
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    content: '',
    type: 'newsletter', // 'newsletter' or 'announcement'
    audience: 'all_users',
    customAudience: '',
    scheduleType: 'now', // 'now' or 'later'
    scheduledFor: '',
    template: 'basic'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const contentEditorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [audienceStats, setAudienceStats] = useState({
    all_users: 0,
    active_subscribers: 0,
    trial_users: 0,
    non_billing_users: 0
  });
  const [audienceStatsLoading, setAudienceStatsLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Helper function to convert date to datetime-local format
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Convert to local timezone and format for datetime-local input
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return localDate.toISOString().slice(0, 16);
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Fetch users function
  const fetchUsers = async () => {
    try {
      setAudienceStatsLoading(true);
      const res = await getAllIndividualUsers();
      console.log("res", res?.data?.users);
      
      if (res?.data?.users && Array.isArray(res.data.users)) {
        const users = res.data.users;
        console.log(`✅ Found ${users.length} users`);
        
        // Store users in state
        setAllUsers(users);
        
        // Set all users as selected by default
        setSelectedUsers(users.map(user => user._id));
        
        // Calculate and update audience stats with real data
        const stats = {
          all_users: users.length,
          active_subscribers: users.filter(user => user.plan && user.plan.name !== 'free').length,
          trial_users: users.filter(user => user.plan && user.plan.name === 'trial').length,
          non_billing_users: users.filter(user => !user.plan || user.plan.name === 'free').length
        };
        
        console.log("📈 Final stats:", stats);
        setAudienceStats(stats);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setAudienceStatsLoading(false);
    }
  };

  // Fetch audience stats when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers(); // This will handle both users and stats
    }
  }, [isOpen]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColorPicker && !event.target.closest('.color-picker-container')) {
        setShowColorPicker(false);
      }
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker, showUserDropdown]);

  const fetchAudienceStats = async () => {
    try {
      setAudienceStatsLoading(true);
      console.log('🔄 Fetching audience stats...');
      // This will be updated by fetchUsers with real data
    } catch (error) {
      console.error('❌ Error fetching audience stats:', error);
    } finally {
      setAudienceStatsLoading(false);
    }
  };

  // Reset form when modal opens/closes or campaign changes
  useEffect(() => {
    if (isOpen) {
      if (isEditing && campaign) {
        // Check for scheduledFor in different possible fields
        const scheduledFor = campaign.scheduledAt || campaign.scheduledFor || campaign.scheduled_for || campaign.scheduleFor;
        
        let scheduledForValue = '';
        let scheduleTypeValue = 'now';
        
        // If campaign status is 'scheduled', it should have a scheduled date
        if (campaign.status === 'scheduled' || scheduledFor) {
          scheduleTypeValue = 'later';
          
          if (scheduledFor) {
            scheduledForValue = formatDateForInput(scheduledFor);
          }
        }
        
        const newFormData = {
          title: campaign.title || '',
          subject: campaign.subject || '',
          content: campaign.content || '',
          type: campaign.type || 'newsletter',
          audience: campaign.audience || 'all_users',
          customAudience: campaign.customAudience || '',
          scheduleType: scheduleTypeValue,
          scheduledFor: scheduledForValue,
          template: campaign.template || 'basic'
        };
        
        setFormData(newFormData);
        
        // Load existing selectedUsers from campaign
        if (campaign.selectedUsers && Array.isArray(campaign.selectedUsers)) {
          // Handle both array of objects and array of IDs
          const userIds = campaign.selectedUsers.map(user => 
            typeof user === 'string' ? user : user._id
          );
          
          // Filter selectedUsers to match the current audience type
          const filteredUserIds = userIds.filter(userId => {
            const user = allUsers.find(u => u._id === userId);
            if (!user) return false;
            
            switch (newFormData.audience) {
              case 'all_users':
                return true;
              case 'active_subscribers':
                return user.plan && user.plan.name !== 'free';
              case 'trial_users':
                return user.plan && user.plan.name === 'trial';
              case 'non_billing_users':
                return !user.plan || user.plan.name === 'free';
              default:
                return false;
            }
          });
          
          setSelectedUsers(filteredUserIds);
          console.log('📝 Loaded and filtered selectedUsers for editing:', {
            original: userIds.length,
            filtered: filteredUserIds.length,
            audience: newFormData.audience
          });
        } else {
          // No existing selectedUsers, select based on audience type
          const filteredUsers = getFilteredUsersForAudience(newFormData.audience);
          if (filteredUsers.length > 0) {
            setSelectedUsers([filteredUsers[0]._id]);
          } else {
            setSelectedUsers([]);
          }
        }
        
        // Set content in the editor after state update
        setTimeout(() => {
          if (contentEditorRef.current && newFormData.content !== contentEditorRef.current.innerHTML) {
            contentEditorRef.current.innerHTML = newFormData.content;
          }
        }, 0);
      } else {
        // New campaign - set default template content
        const defaultContent = DEFAULT_TEMPLATES['newsletter']; // Default to newsletter template
        
        setFormData({
          title: '',
          subject: '',
          content: defaultContent,
          type: 'newsletter',
          audience: 'all_users',
          customAudience: '',
          scheduleType: 'now',
          scheduledFor: '',
          template: 'basic'
        });
        
        // Clear selected users
        setSelectedUsers([]);
        
        // Set default content in editor
        setTimeout(() => {
          if (contentEditorRef.current) {
            contentEditorRef.current.innerHTML = defaultContent;
          }
        }, 0);
      }
      setErrors({});
    }
  }, [isOpen, isEditing, campaign]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Handle email type change - load default template content
    if (name === 'type' && !isEditing) {
      // Only auto-load template for new campaigns, not when editing
      const defaultContent = DEFAULT_TEMPLATES[value] || '';
      setFormData(prev => ({
        ...prev,
        [name]: value,
        content: defaultContent
      }));
      
      // Update content editor
      setTimeout(() => {
        if (contentEditorRef.current) {
          contentEditorRef.current.innerHTML = defaultContent;
        }
      }, 0);
      
      console.log(`📝 Loaded default template for: ${value}`);
      return; // Early return to avoid duplicate state update
    }
    
    // Handle audience change - filter and select users based on audience type
    if (name === 'audience' && value !== 'custom') {
      const filteredUsers = getFilteredUsersForAudience(value);
      
      if (!isEditing) {
        // For new campaigns: select first user by default
        if (filteredUsers.length > 0) {
          setSelectedUsers([filteredUsers[0]._id]);
        } else {
          setSelectedUsers([]);
        }
      } else {
        // For editing campaigns: keep only users that match the new audience type
        const currentSelectedUsers = allUsers.filter(user => selectedUsers.includes(user._id));
        const validUsers = currentSelectedUsers.filter(user => {
          switch (value) {
            case 'all_users':
              return true;
            case 'active_subscribers':
              return user.plan && user.plan.name !== 'free';
            case 'trial_users':
              return user.plan && user.plan.name === 'trial';
            case 'non_billing_users':
              return !user.plan || user.plan.name === 'free';
            default:
              return false;
          }
        });
        
        setSelectedUsers(validUsers.map(user => user._id));
        console.log(`🔄 Audience changed to ${value}, filtered selectedUsers:`, validUsers.length);
      }
      
      setUserSearchQuery('');
      setShowUserDropdown(false);
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Campaign title is required';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Email subject is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Email content is required';
    }
    
    if (formData.audience === 'custom' && !formData.customAudience.trim()) {
      newErrors.customAudience = 'Custom audience description is required';
    }
    
    if (formData.scheduleType === 'later' && !formData.scheduledFor) {
      newErrors.scheduledFor = 'Scheduled date and time is required';
    }
    
    if (formData.scheduleType === 'later' && formData.scheduledFor) {
      const scheduledDate = new Date(formData.scheduledFor);
      const now = new Date();
      if (scheduledDate <= now) {
        newErrors.scheduledFor = 'Scheduled time must be in the future';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const campaignData = {
        ...formData,
        scheduledFor: formData.scheduleType === 'later' ? formData.scheduledFor : null,
        audienceCount: selectedUsers.length,
        selectedUsers: selectedUsers, // ✅ Always send selectedUsers array
        attachments: attachments.map(att => ({
          name: att.name,
          size: att.size,
          type: att.type
          // Note: In real implementation, you'd upload files to server first
          // and store file URLs/IDs here instead of the actual file objects
        }))
      };
      
      console.log('📤 Submitting campaign data:', {
        ...campaignData,
        selectedUsersCount: selectedUsers.length,
        selectedUsersArray: selectedUsers
      });
      await onSave(campaignData);
    } catch (error) {
      console.error('Error saving campaign:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get filtered users based on selected audience
  const getFilteredUsers = () => {
    switch (formData.audience) {
      case 'all_users':
        return allUsers;
      case 'active_subscribers':
        return allUsers.filter(user => user.plan && user.plan.name !== 'free');
      case 'trial_users':
        return allUsers.filter(user => user.plan && user.plan.name === 'trial');
      case 'non_billing_users':
        return allUsers.filter(user => !user.plan || user.plan.name === 'free');
      default:
        return [];
    }
  };

  // Helper function to get filtered users for specific audience
  const getFilteredUsersForAudience = (audience) => {
    switch (audience) {
      case 'all_users':
        return allUsers;
      case 'active_subscribers':
        return allUsers.filter(user => user.plan && user.plan.name !== 'free');
      case 'trial_users':
        return allUsers.filter(user => user.plan && user.plan.name === 'trial');
      case 'non_billing_users':
        return allUsers.filter(user => !user.plan || user.plan.name === 'free');
      default:
        return [];
    }
  };

  const getAudienceCount = (audience) => {
    return audienceStats[audience] || 0;
  };

  // Get filtered users based on search query
  const getSearchFilteredUsers = () => {
    const filteredUsers = getFilteredUsers();
    if (!userSearchQuery) return filteredUsers;
    
    return filteredUsers.filter(user => 
      user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
    );
  };

  // Handle user selection/deselection
  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Get selected user names for display
  const getSelectedUserNames = () => {
    const selectedUserObjects = allUsers.filter(user => selectedUsers.includes(user._id));
    if (selectedUserObjects.length === 0) return 'Select users...';
    if (selectedUserObjects.length === 1) return selectedUserObjects[0].name;
    return `${selectedUserObjects.length} users selected`;
  };

  // Handle individual user selection
  const handleUserSelection = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  // Handle select all / deselect all
  const handleSelectAllUsers = (isSelected) => {
    if (isSelected) {
      setSelectedUsers(allUsers.map(user => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Rich text editor functions
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    contentEditorRef.current?.focus();
  };

  const handleContentChange = () => {
    if (contentEditorRef.current) {
      const content = contentEditorRef.current.innerHTML;
      setFormData(prev => ({ ...prev, content }));
    }
  };

  const handleContentInput = (e) => {
    // Get the current content
    const content = e.target.innerHTML;
    
    // Update state without triggering re-render of the editor
    setFormData(prev => {
      if (prev.content !== content) {
        return { ...prev, content };
      }
      return prev;
    });
  };

  // Separate function for blur event to ensure content is saved
  const handleContentBlur = () => {
    if (contentEditorRef.current) {
      const content = contentEditorRef.current.innerHTML;
      setFormData(prev => ({ ...prev, content }));
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      formatText('createLink', url);
    }
  };

  const handleColorChange = (color) => {
    formatText('foreColor', color);
    setShowColorPicker(false);
  };

  const handleFileAttachment = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }));
    
    setAttachments(prev => [...prev, ...newAttachments]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  // const insertImage = () => {
  //   const url = prompt('Enter image URL:');
  //   if (url) {
  //     formatText('insertImage', url);
  //   }
  // };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setShowPreview(true);
      
      // Prepare preview data matching backend API format
      const previewData = {
        subject: formData.subject || 'No subject',
        content: formData.content || '<p>No content</p>',
        type: formData.type || 'newsletter', // "newsletter" or "announcement"
        recipientName: 'Preview User' // Default name for preview
      };
      
      console.log('📧 Fetching email preview...', previewData);
      
      // Call backend API to get full template HTML
      const response = await previewCampaignEmail(previewData);
      
      console.log('📧 Preview response:', response);
      
      // Backend returns HTML directly in response.data
      if (response?.data) {
        // If response.data is string (HTML), use it directly
        const html = typeof response.data === 'string' ? response.data : response.data.html;
        setPreviewHtml(html);
        console.log('✅ Preview HTML loaded');
      } else {
        console.error('❌ No HTML in preview response');
        setPreviewHtml('<p>Failed to load preview</p>');
      }
    } catch (error) {
      console.error('❌ Error loading preview:', error);
      setPreviewHtml('<p>Error loading preview</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  const getAudienceLabel = (audience) => {
    const labels = {
      all_users: 'All Users',
      active_subscribers: 'Active Subscribers',
      trial_users: 'Trial Users',
      non_billing_users: 'Non-billing Users',
      custom: 'Custom Segment'
    };
    return labels[audience] || audience;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-60 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-bd-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Mail size={20} className="text-ac-02" />
            <h2 className="text-lg font-semibold text-fg-50">
              {isEditing ? 'Edit Campaign' : 'Create New Campaign'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-fg-60 hover:text-fg-50 hover:bg-bg-40 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
            {/* Email Type */}
            <div>
              <label className="block text-sm font-medium text-fg-50 mb-2">
                Email Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm border border-bd-50 rounded-lg bg-bg-60 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent"
              >
                <option value="newsletter">📰 Newsletter</option>
                <option value="announcement">📢 Announcement</option>
                <option value="customer_notification">🔔 Customer Notification</option>
              </select>
              {/* <p className="text-xs text-fg-60 mt-1">
                {formData.type === 'newsletter' 
                  ? 'Newsletter: Regular updates, friendly tone, blue theme' 
                  : formData.type === 'announcement'
                  ? 'Announcement: Important updates, formal tone, purple theme'
                  : 'Customer Notification: Account updates, transactional messages'}
              </p> */}
            </div>

            {/* Campaign Title */}
            <div>
              <label className="block text-sm font-medium text-fg-50 mb-2">
                Campaign Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Weekly Product Update"
                className={`w-full px-3 py-2 text-sm border rounded-lg bg-bg-60 text-fg-50 placeholder-fg-60 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent ${
                  errors.title ? 'border-red-500' : 'border-bd-50'
                }`}
              />
              {errors.title && (
                <p className="text-red-500 text-xs mt-1">{errors.title}</p>
              )}
            </div>

            {/* Audience Selection */}
            <div>
              <label className="block text-sm font-medium text-fg-50 mb-2">
                Target Audience *
                {audienceStatsLoading && (
                  <span className="ml-2 text-xs text-fg-60">(Loading counts...)</span>
                )}
              </label>
              <select
                name="audience"
                value={formData.audience}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm border border-bd-50 rounded-lg bg-bg-60 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent"
                disabled={audienceStatsLoading}
              >
                <option value="all_users">
                  All Users ({audienceStatsLoading ? '...' : getAudienceCount('all_users').toLocaleString()})
                </option>
                <option value="active_subscribers">
                  Active Subscribers ({audienceStatsLoading ? '...' : getAudienceCount('active_subscribers').toLocaleString()})
                </option>
                <option value="trial_users">
                  Trial Users ({audienceStatsLoading ? '...' : getAudienceCount('trial_users').toLocaleString()})
                </option>
                <option value="non_billing_users">
                  Non-billing Users ({audienceStatsLoading ? '...' : getAudienceCount('non_billing_users').toLocaleString()})
                </option>
                <option value="custom">Custom Segment</option>
              </select>
              
              {/* Searchable Multi-Select Users Dropdown */}
              {formData.audience !== 'custom' && getFilteredUsers().length > 0 && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-fg-50 mb-2">
                    Select Users
                  </label>
                  
                  {/* Custom Multi-Select Dropdown */}
                  <div className="relative user-dropdown-container">
                    {/* Display Selected Users */}
                    <div
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                      className="w-full px-3 py-2 text-sm border border-bd-50 rounded-lg bg-bg-60 text-fg-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent min-h-[40px] flex items-center justify-between"
                    >
                      <span className={selectedUsers.length === 0 ? 'text-fg-60' : 'text-fg-50'}>
                        {getSelectedUserNames()}
                      </span>
                      <svg className={`w-4 h-4 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {/* Dropdown Content */}
                    {showUserDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-bg-60 border border-bd-50 rounded-lg shadow-lg max-h-84 overflow-hidden">
                        {/* Search Input */}
                        <div className="p-2 border-b border-bd-50">
                          <input
                            type="text"
                            placeholder="Search users..."
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            className="w-full px-2 py-1 text-sm bg-bg-50 border border-bd-50 rounded focus:outline-none focus:ring-1 focus:ring-ac-02 text-fg-50 placeholder-fg-60"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="p-2 border-b border-bd-50 flex gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUsers(getFilteredUsers().map(user => user._id));
                            }}
                            className="text-xs text-ac-02 hover:text-ac-01 px-2 py-1 border border-ac-02 rounded hover:bg-ac-02 hover:text-white transition-colors"
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUsers([]);
                            }}
                            className="text-xs text-fg-60 hover:text-fg-50 px-2 py-1 border border-bd-50 rounded hover:bg-bg-40 transition-colors"
                          >
                            Clear All
                          </button>
                        </div>
                        
                        {/* User List */}
                        <div className="max-h-60 overflow-y-auto custom-scrollbar">
                          {getSearchFilteredUsers().map((user) => (
                            <div
                              key={user._id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUserToggle(user._id);
                              }}
                              className="flex items-start gap-3 p-3 hover:bg-bg-50 cursor-pointer border-b border-bd-50 last:border-b-0"
                            >
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user._id)}
                                onChange={() => {}} // Handled by parent onClick
                                className="w-4 h-4 mt-0.5 text-ac-02 bg-bg-60 border-bd-50 rounded focus:ring-ac-02 focus:ring-2 pointer-events-none flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-fg-50 break-words">
                                  {user.name}
                                </div>
                                <div className="text-xs text-fg-60 break-words mt-0.5">
                                  {user.email}
                                </div>
                                {user.plan && (
                                  <div className="text-xs text-ac-02 mt-0.5">
                                    Plan: {user.plan.name}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {getSearchFilteredUsers().length === 0 && (
                            <div className="p-4 text-center text-fg-60 text-sm">
                              No users found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {formData.audience === 'custom' && (
                <div className="mt-2">
                  <input
                    type="text"
                    name="customAudience"
                    value={formData.customAudience}
                    onChange={handleInputChange}
                    placeholder="Describe your custom audience segment"
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-bg-60 text-fg-50 placeholder-fg-60 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent ${
                      errors.customAudience ? 'border-red-500' : 'border-bd-50'
                    }`}
                  />
                  {errors.customAudience && (
                    <p className="text-red-500 text-xs mt-1">{errors.customAudience}</p>
                  )}
                </div>
              )}
            </div>

                        {/* Email Subject */}
            <div>
              <label className="block text-sm font-medium text-fg-50 mb-2">
                Email Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="e.g., New Features This Week"
                className={`w-full px-3 py-2 text-sm border rounded-lg bg-bg-60 text-fg-50 placeholder-fg-60 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent ${
                  errors.subject ? 'border-red-500' : 'border-bd-50'
                }`}
              />
              {errors.subject && (
                <p className="text-red-500 text-xs mt-1">{errors.subject}</p>
              )}
            </div>

            {/* Email Content */}
            <div>
              <label className="block text-sm font-medium text-fg-50 mb-2">
                Email Content *
              </label>
              
              {/* Rich Text Editor Toolbar */}
              <div className="border border-bd-50 rounded-t-lg bg-bg-50 p-2 flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => formatText('bold')}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Bold"
                >
                  <Bold size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => formatText('italic')}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Italic"
                >
                  <Italic size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => formatText('underline')}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Underline"
                >
                  <Underline size={16} />
                </button>
                
                <div className="w-px h-6 bg-bd-50 mx-1"></div>
                
                {/* Color Picker */}
                <div className="relative color-picker-container">
                  <button
                    type="button"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className="p-2 hover:bg-bg-40 rounded transition-colors"
                    title="Text Color"
                  >
                    <Palette size={16} />
                  </button>
                  
                  {showColorPicker && (
                    <div className="absolute top-10 left-0 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-10 w-64">
                      {/* Grayscale Colors */}
                      <div className="mb-3">
                        <div className="text-xs text-gray-600 mb-2">Grayscale</div>
                        <div className="flex gap-1">
                          {['#000000', '#404040', '#808080', '#BFBFBF', '#E0E0E0', '#FFFFFF'].map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => handleColorChange(color)}
                              className="w-8 h-8 rounded border border-gray-300 hover:scale-110 transition-transform shadow-sm"
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Standard Colors */}
                      <div className="mb-3">
                        <div className="text-xs text-gray-600 mb-2">Standard Colors</div>
                        <div className="grid grid-cols-6 gap-1">
                          {[
                            '#FF0000', '#FF8000', '#FFFF00', '#00FF00', '#0080FF', '#8000FF',
                            '#FF0080', '#FF4000', '#80FF00', '#00FF80', '#0040FF', '#4000FF'
                          ].map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => handleColorChange(color)}
                              className="w-8 h-8 rounded border border-gray-300 hover:scale-110 transition-transform shadow-sm"
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Theme Colors */}
                      <div className="mb-3">
                        <div className="text-xs text-gray-600 mb-2">Theme Colors</div>
                        <div className="space-y-1">
                          {/* Light variants */}
                          <div className="flex gap-1">
                            {['#FFE6E6', '#FFE6CC', '#FFFFE6', '#E6FFE6', '#E6F3FF', '#F0E6FF'].map(color => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => handleColorChange(color)}
                                className="w-8 h-6 border border-gray-300 hover:scale-105 transition-transform"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                          {/* Medium variants */}
                          <div className="flex gap-1">
                            {['#FFCCCC', '#FFCC99', '#FFFFCC', '#CCFFCC', '#CCE6FF', '#E0CCFF'].map(color => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => handleColorChange(color)}
                                className="w-8 h-6 border border-gray-300 hover:scale-105 transition-transform"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                          {/* Dark variants */}
                          <div className="flex gap-1">
                            {['#CC0000', '#CC6600', '#CCCC00', '#00CC00', '#0066CC', '#6600CC'].map(color => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => handleColorChange(color)}
                                className="w-8 h-6 border border-gray-300 hover:scale-105 transition-transform"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                          {/* Darker variants */}
                          <div className="flex gap-1">
                            {['#800000', '#804000', '#808000', '#008000', '#004080', '#400080'].map(color => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => handleColorChange(color)}
                                className="w-8 h-6 border border-gray-300 hover:scale-105 transition-transform"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Custom Color Input */}
                      <div className="border-t border-gray-200 pt-3">
                        <div className="text-xs text-gray-600 mb-2">Custom Color</div>
                        <div className="flex gap-2 items-center">
                          <input
                            type="color"
                            onChange={(e) => handleColorChange(e.target.value)}
                            className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                            title="Choose custom color"
                          />
                          <input
                            type="text"
                            placeholder="#000000"
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const color = e.target.value;
                                if (/^#[0-9A-F]{6}$/i.test(color)) {
                                  handleColorChange(color);
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="w-px h-6 bg-bd-50 mx-1"></div>
                
                <button
                  type="button"
                  onClick={() => formatText('justifyLeft')}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Align Left"
                >
                  <AlignLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => formatText('justifyCenter')}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Align Center"
                >
                  <AlignCenter size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => formatText('justifyRight')}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Align Right"
                >
                  <AlignRight size={16} />
                </button>
                
                <div className="w-px h-6 bg-bd-50 mx-1"></div>
                
                <button
                  type="button"
                  onClick={() => formatText('insertUnorderedList')}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Bullet List"
                >
                  <List size={16} />
                </button>
                <button
                  type="button"
                  onClick={insertLink}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Insert Link"
                >
                  <Link size={16} />
                </button>
                {/* <button
                  type="button"
                  onClick={insertImage}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Insert Image"
                >
                  <Image size={16} />
                </button> */}
                
                <div className="w-px h-6 bg-bd-50 mx-1"></div>
                
                <button
                  type="button"
                  onClick={handleFileAttachment}
                  className="p-2 hover:bg-bg-40 rounded transition-colors"
                  title="Attach File"
                >
                  <Paperclip size={16} />
                </button>
                
                <div className="w-px h-6 bg-bd-50 mx-1"></div>
                
                <select
                  onChange={(e) => formatText('fontSize', e.target.value)}
                  className="px-2 py-1 text-xs bg-bg-60 border border-bd-50 rounded"
                  defaultValue="3"
                >
                  <option value="1">Small</option>
                  <option value="3">Normal</option>
                  <option value="5">Large</option>
                  <option value="7">Extra Large</option>
                </select>
              </div>
              
              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Attachments Display */}
              {attachments.length > 0 && (
                <div className="border-x border-bd-50 bg-bg-50 p-2">
                  <div className="text-xs text-fg-60 mb-2">Attachments:</div>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map(attachment => (
                      <div key={attachment.id} className="flex items-center gap-2 bg-bg-40 px-2 py-1 rounded text-xs">
                        <Paperclip size={12} />
                        <span className="text-fg-50">{attachment.name}</span>
                        <span className="text-fg-60">({(attachment.size / 1024).toFixed(1)}KB)</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(attachment.id)}
                          className="text-red-500 hover:text-red-700 ml-1"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Content Editor */}
              <div className="relative">
                <div
                  ref={contentEditorRef}
                  contentEditable
                  onInput={handleContentInput}
                  onBlur={handleContentBlur}
                  suppressContentEditableWarning={true}
                  className={`w-full min-h-[200px] px-3 py-2 text-sm border border-t-0 border-bd-50 rounded-b-lg bg-bg-60 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent ${
                    errors.content ? 'border-red-500' : ''
                  }`}
                  style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    lineHeight: '1.5'
                  }}
                />
                {/* Placeholder */}
                {!formData.content && (
                  <div 
                    className="absolute top-2 left-3 text-fg-60 pointer-events-none text-sm"
                    style={{ lineHeight: '1.5' }}
                  >
                    Write your email content here...
                  </div>
                )}
              </div>
              
              {errors.content && (
                <p className="text-red-500 text-xs mt-1">{errors.content}</p>
              )}
            </div>

            {/* Schedule Options */}
            <div>
              <label className="block text-sm font-medium text-fg-50 mb-2">
                Send Options
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="scheduleType"
                    value="now"
                    checked={formData.scheduleType === 'now'}
                    onChange={handleInputChange}
                    className="mr-2 text-ac-02 focus:ring-ac-02"
                  />
                  <span className="text-sm text-fg-50">Save as draft</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="scheduleType"
                    value="later"
                    checked={formData.scheduleType === 'later'}
                    onChange={handleInputChange}
                    className="mr-2 text-ac-02 focus:ring-ac-02"
                  />
                  <span className="text-sm text-fg-50">Schedule for later</span>
                </label>
              </div>
              
              {formData.scheduleType === 'later' && (
                <div className="mt-3">
                  <input
                    type="datetime-local"
                    name="scheduledFor"
                    value={formData.scheduledFor}
                    onChange={handleInputChange}
                    min={new Date().toISOString().slice(0, 16)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-bg-60 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent ${
                      errors.scheduledFor ? 'border-red-500' : 'border-bd-50'
                    }`}
                  />
                  {errors.scheduledFor && (
                    <p className="text-red-500 text-xs mt-1">{errors.scheduledFor}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-bd-50 bg-bg-50 flex-shrink-0">
            <button
              type="button"
              onClick={handlePreview}
              className="flex items-center gap-2 px-4 py-2 text-sm text-fg-60 hover:text-fg-50 hover:bg-bg-40 rounded-lg transition-colors"
            >
              <Eye size={16} />
              Preview
            </button>
            
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-fg-60 hover:text-fg-50 hover:bg-bg-40 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-ac-02 text-white rounded-lg hover:bg-ac-01 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={16} />
                {isSubmitting ? 'Saving...' : isEditing ? 'Update Campaign' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </form>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
              {/* Preview Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.type === 'newsletter' 
                      ? '📰 Newsletter Template' 
                      : formData.type === 'announcement'
                      ? '📢 Announcement Template'
                      : '🔔 Customer Notification Template'}
                  </p>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Preview Content - Full Template in iframe */}
              <div className="flex-1 overflow-hidden bg-gray-100">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading preview...</p>
                    </div>
                  </div>
                ) : previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full h-full border-0"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">No preview available</p>
                  </div>
                )}
              </div>
              
              {/* Preview Footer */}
              {/* <div className="flex items-center justify-between p-4 border-t bg-gray-50">
                <div className="text-xs text-gray-600">
                  <p>Audience: {getAudienceLabel(formData.audience)} ({selectedUsers.length} recipients)</p>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close Preview
                </button>
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddNewsletterModal;